import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { API_URL } from "../config";

const AuthContext = createContext(null);

const TOKEN_KEY = "dnah_token";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ================================
     CHECK TOKEN ON LOAD
  ================================ */
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!token) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (!cancelled) {
          if (res.ok && data?.user) {
            setUser(data.user);
          } else {
            // ❌ invalid token → clear everything
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setUser(null);
          }

          setLoading(false);
        }
      } catch (err) {
        console.error("Auth error:", err);

        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
          setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [token]);

  /* ================================
     AUTH FUNCTIONS
  ================================ */
  const value = useMemo(() => {
    return {
      token,
      user,
      loading,

      // ✅ helpers
      isAuthed: Boolean(token && user),
      isAdmin: user?.role === "admin",

      /* 🔐 LOGIN */
      async login(email, password) {
        const res = await fetch(`${API_URL}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });

        let data = {};
        try {
          data = await res.json();
        } catch {
          throw new Error("Server error");
        }

        if (!res.ok) {
          throw new Error(data?.error || "Login failed");
        }

        // ✅ SAVE TOKEN
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
        setUser(data.user);

        return data.user;
      },

      /* 🚪 LOGOUT */
      logout() {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);

        // 🔥 force redirect (clean UX)
        window.location.href = "/login";
      },
    };
  }, [token, user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/* ================================
   HOOK
================================ */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}